import styled from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { Button, ButtonBase } from "./Button";
import { OverflowText } from "./OverflowText";
import { Table, TableCell, TableHeaderCell } from "./Table";
import { Input, Textarea } from "./Form";

export const ScorecardTableWrapper = styled.div`
  border: ${vars.borderWidth.thin} solid var(--inzeloop-ds-color-border-quiet);
  border-radius: ${vars.radius.sm};
`;

export const ScorecardTable = styled(Table).attrs({ $minWidth: "0" })`
  width: 100%;
`;

export const ScorecardTableHeader = styled(TableHeaderCell)`
  top: 0;
  padding: var(--inzeloop-ds-spacing-md);
  border-bottom: ${vars.borderWidth.medium} solid
    var(--inzeloop-ds-color-border-control);
  background: var(--inzeloop-ds-color-border-table-header);
  font-size: var(--inzeloop-ds-font-size-14px);
  font-weight: ${vars.typography.weight.semibold};
  text-align: left;
`;

export const ScorecardTableCell = styled(TableCell)`
  padding: var(--inzeloop-ds-spacing-sm) var(--inzeloop-ds-spacing-sm)
    var(--inzeloop-ds-spacing-sm) 0;
  border-bottom: ${vars.borderWidth.thin} solid
    var(--inzeloop-ds-color-border-quiet);
`;

export const ScorecardInput = styled(Input)`
  width: 95%;
  height: 25px;
  border: ${vars.borderWidth.thin} solid var(--inzeloop-ds-color-border-control);
  border-radius: ${vars.radius.xs};
  font-size: var(--inzeloop-ds-font-size-14-4px);
  vertical-align: middle;
`;

export const ScorecardTextarea = styled(Textarea)`
  width: 95%;
  min-height: 60px;
  border: ${vars.borderWidth.thin} solid var(--inzeloop-ds-color-border-control);
  border-radius: ${vars.radius.xs};
  font-size: var(--inzeloop-ds-font-size-13-6px);
  resize: none;
`;

export const ScorecardValidateButton = styled(Button).attrs({
  variant: "primary",
  size: "lg",
})`
  width: 120px;
  padding: var(--inzeloop-ds-spacing-20px);
  margin: var(--inzeloop-ds-spacing-15px) auto 0;
  border-radius: ${vars.radius.xl};
  font-size: var(--inzeloop-ds-font-size-keyword-large);
`;

export const ScorecardEditButton = styled(ButtonBase)<{
  disabled?: boolean;
}>`
  position: relative;
  display: flex;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  align-self: flex-end;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: ${vars.spacing.sm};
  border: var(--inzeloop-ds-color-border-transparent);
  background: var(--inzeloop-ds-color-background-transparent);
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "default")};

  &::before {
    position: absolute;
    inset: -10px;
    content: "";
  }

  @media (min-width: 834px) {
    &::before {
      inset: -14px -6px -6px -14px;
    }
  }
`;

export const ScorecardSection = styled.section`
  width: 100%;
  box-sizing: border-box;
`;

export const ScorecardList = styled.div`
  display: grid;
  width: 100%;
  align-items: start;
`;

export const ScorecardRow = styled.article<{
  $rowWidth: number;
  $textColor: string;
}>`
  display: grid;
  width: ${({ $rowWidth }) => `${$rowWidth.toFixed(2)}%`};
  height: 118px;
  grid-template-columns: 150px minmax(0, 1fr);
  box-sizing: border-box;
  overflow: hidden;
  border-right: ${vars.borderWidth.thin} solid
    var(--inzeloop-ds-color-border-light);
  border-bottom: ${vars.borderWidth.thin} solid
    var(--inzeloop-ds-color-border-light);
  border-left: 5px solid ${({ $textColor }) => $textColor};
  border-radius: 0 ${vars.radius.sm} ${vars.radius.sm} 0;
  background: ${vars.color.background.surface};
  color: ${({ $textColor }) => $textColor};
  transition:
    width 250ms ease,
    background-color ${vars.motion.normal} ease;

  &:first-child {
    border-top: ${vars.borderWidth.thin} solid
      var(--inzeloop-ds-color-border-light);
    border-radius: ${vars.radius.sm} ${vars.radius.sm} ${vars.radius.sm} 0;
  }

  &:last-child {
    border-radius: 0 ${vars.radius.sm} ${vars.radius.sm} ${vars.radius.sm};
  }

  &:only-child {
    border-radius: ${vars.radius.sm};
  }

  &:hover {
    background: ${vars.color.background.subtle};
  }

  @media (max-width: 680px) {
    height: 106px;
    grid-template-columns: 104px minmax(0, 1fr);
    border-left-width: ${vars.borderWidth.thick};
  }

  @media print {
    height: 24mm;
    grid-template-columns: 26mm minmax(0, 1fr);
    break-inside: avoid;
  }
`;

export const ScorecardWeight = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  border-right: ${vars.borderWidth.thin} solid
    var(--inzeloop-ds-color-border-light);
  background: ${vars.color.background.surface};
`;

export const ScorecardWeightValue = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${vars.spacing.xxs};
  font-family: ${vars.typography.family.sans};
  font-size: clamp(
    var(--inzeloop-ds-font-size-42px),
    var(--inzeloop-ds-font-size-5vw),
    var(--inzeloop-ds-font-size-58px)
  );
  font-weight: ${vars.typography.weight.bold};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.06em;
  line-height: var(--inzeloop-ds-line-height-condensed);

  span {
    font-size: var(--inzeloop-ds-font-size-relative-half);
    font-weight: ${vars.typography.weight.semibold};
    letter-spacing: -0.02em;
  }

  @media (max-width: 680px) {
    font-size: var(--inzeloop-ds-font-size-34px);
  }
`;

export const ScorecardCopy = styled.div`
  display: grid;
  min-width: 0;
  align-self: center;
  grid-template-rows: 32px 56px;
  row-gap: var(--inzeloop-ds-spacing-6px);
  padding: ${vars.spacing.sm} var(--inzeloop-ds-spacing-36px);

  @media (max-width: 680px) {
    padding: ${vars.spacing.sm} var(--inzeloop-ds-spacing-18px)
      ${vars.spacing.sm} var(--inzeloop-ds-spacing-22px);
  }

  @media print {
    padding: var(--inzeloop-ds-spacing-2mm) var(--inzeloop-ds-spacing-6mm);
  }
`;

export const ScorecardCriterion = styled.h2`
  max-width: 700px;
  overflow: hidden;
  margin: 0;
  color: inherit;
  font-family: ${vars.typography.family.sans};
  font-size: clamp(
    var(--inzeloop-ds-font-size-lg),
    2.2vw,
    var(--inzeloop-ds-font-size-25px)
  );
  font-weight: ${vars.typography.weight.semibold};
  line-height: var(--inzeloop-ds-line-height-snug);
  letter-spacing: -0.02em;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 680px) {
    font-size: var(--inzeloop-ds-font-size-18px);
  }

  @media print {
    font-size: var(--inzeloop-ds-font-size-14px);
  }
`;

export const ScorecardDescription = styled(OverflowText)`
  height: 56px;
  max-width: 720px;
  overflow: hidden;
  margin: 0;
  color: inherit;
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.regular};
  line-height: var(--inzeloop-ds-line-height-lg);
`;

export const ScorecardState = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: var(--inzeloop-ds-spacing-56px) var(--inzeloop-ds-spacing-28px);
  border: ${vars.borderWidth.thin} solid var(--inzeloop-ds-color-border-light);
  border-radius: ${vars.radius.sm};
  background: ${vars.color.background.surface};
  color: ${vars.color.text.muted};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  line-height: var(--inzeloop-ds-line-height-lg);
  text-align: center;
`;
